import React, {
  useMemo,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import styles from "./account-details.module.scss";
import { useHistory } from "react-router-dom";
import { useForm, useApi, useModal, useMount } from "hooks";
import { initialState as formState } from "./account-details.state";
import {
  isEmailValid,
  formatDate,
  handleRequest,
  fleetAccountForm,
  getObjectValues,
  getObjectDifference,
  isTelephoneNumber,
  maxLength,
  capitalizeAll,
} from "utils";
import { ConfirmModal, SuccessModal } from "components/modals";
import { Loader } from "components/commons";
import {
  createFleetAccount,
  updateFleetAccount,
  getFleet,
  getPricingAccumulatedMovement,
} from "apis";
import {
  FormMode,
  Path,
  AccountStatus,
  DateTime,
  FleetAccountType,
  PLBTypes,
  IntroductoryPromoOffer,
} from "enums";
import locale from "localization";
import AccountDetailsFormModule from "./account-details-form.module";
import EditConfirm from "../account-edit/account-edit-confirm";
import { Provinces, Cities } from "assets";
import { SuccessFormSubmit, SuccessUpdate } from "images";
import classNames from "classnames";
import _ from "lodash";
import { getStationPricing } from "apis/station.api";
import Big from "big.js";
import Validation from "utils/validation.utils";
import { prettifyIntroductoryPromoOffer } from "utils/pretty.utils";

const AccountDetailsModule = forwardRef(({ pageMode, fleetId, ...props }, ref) => {
  const { setUpdateLoading, setDisableUpdate } = props;
  const viewMode = pageMode === FormMode.View;
  const addMode = pageMode === FormMode.Add;
  const editMode = pageMode === FormMode.Edit;
  const [switchStatus, setSwitchStatus] = useState(false);
  const [provinceKeyFilter, setProvinceKeyFilter] = useState("");
  const [previousFormValues, setPreviousFormValues] = useState({});
  const [editConfirmData, setEditConfirmData] = useState({});
  const [stationDepotName, setStationDepotName] = useState({});
  const [otherDetails, setOtherDetails] = useState({
    status: "",
    dateOnboard: "",
  });
  const [newEmailAdress, setNewEmailAddress] = useState({
    flag: false,
    emailAddresses: [],
    removedEmailAddresses: [],
  });
  const [stationsSelected, setStationsSelected] = useState([]);
  const [fleetCardSelected, setFleetCardSelected] = useState([]);
  const [discountCombination, setDiscountCombination] = useState([
    {
      stationIds: {
        name: "stationIds",
        visible: false,
        label: locale.eligibleReferenceStation,
        allLabel: locale.referenceStation,
        isAll: true,
        isMultiple: true,
        value: [],
        placeholder: locale.searchOrSelect,
        disableAllOption: true,
        selectAllLabel: locale.all,
        allSelectedText: `All ${locale.referenceStation}`,
      },
      discountDiesel: {
        name: "discountDiesel",
        required: true,
        validations: [Validation.required({ noMessage: true })],
        innerLabel: true,
        parseNumber: true,
        value: "",
        placeholder: "0.00",
      },
      discountGas91: {
        name: "discountGas91",
        required: true,
        validations: [Validation.required({ noMessage: true })],
        innerLabel: true,
        parseNumber: true,
        value: "",
        placeholder: "0.00",
      },
      discountGas95: {
        name: "discountGas95",
        required: true,
        validations: [Validation.required({ noMessage: true })],
        innerLabel: true,
        parseNumber: true,
        value: "",
        placeholder: "0.00",
      },
      discountGas97: {
        name: "discountGas97",
        required: true,
        validations: [Validation.required({ noMessage: true })],
        innerLabel: true,
        parseNumber: true,
        value: "",
        placeholder: "0.00",
      },
    },
  ]);
  const inputRef = useRef();

  const getPriceMovement = useApi({
    api: getPricingAccumulatedMovement,
  });

  const {
    request: getFleetRequest,
    loading: loadingFleet,
    result: getFleetResult,
  } = useApi({
    api: getFleet,
    modalError: true,
    params: {
      fleetId,
    },
  });

  const {
    request: getStationPricingRequest,
    loading: loadingStationPricing,
    result: getStationPricingResult,
  } = useApi({
    api: getStationPricing,
    modalError: true,
  });

  const computeMovement = useCallback((latestPumpPrice, basePrice) => {
    return Big(latestPumpPrice).minus(Big(basePrice)).round(2).toNumber();
  }, []);

  const computeContractPrice = useCallback((basePrice, movement, discount) => {
    return Big(basePrice || 0)
      .add(Big(movement || 0))
      .minus(Big(discount || 0))
      .round(2)
      .toNumber();
  }, []);

  const form = useMemo(() => {
    let initialState = {};
    if (fleetId) {
      const data = getFleetResult;
      const pricingData = getStationPricingResult;

      if (data) {
        initialState = fleetAccountForm({
          data,
          viewMode,
          editMode,
          pricingData,
          computeMovement,
          computeContractPrice,
        });
        if (editMode) {
          setPreviousFormValues(_.cloneDeep(getObjectValues(initialState)));
          const checked = data.status === AccountStatus.Deactivated;
          setSwitchStatus(checked);
        }
        setOtherDetails({
          status: data.status,
          dateOnboard: formatDate(data.dateTimeOnboarded, DateTime.E),
          referenceStationType: data?.referenceStation?.stationType || null,
          depot: data?.depot?.name || null,
        });
      }
    }
    return formState(initialState);
  }, [
    editMode,
    fleetId,
    getFleetResult,
    viewMode,
    getStationPricingResult,
    computeMovement,
    computeContractPrice,
  ]);

  const confirmModal = useModal();
  const { close } = confirmModal;
  const history = useHistory();

  useMount(async () => {
    if (fleetId) {
      const res = await getFleetRequest();

      if (res.priceAssessmentDate && (viewMode || editMode)) {
        getStationPricingRequest({
          stationId: res.referenceStation.stationId,
          effectiveDate: res.priceAssessmentDate,
        });

        getPriceMovement?.request({
          effectiveAt: res.priceAssessmentDate,
        });
      }
    }
  });

  const {
    fields,
    modifyField,
    modifyForm,
    isFormSubmittable,
    submitForm,
    clearForm,
    getFormValues,
    applyFieldErrors,
    validatedField,
  } = useForm({
    initialState: form,
  });

  const addRequest = useApi({
    api: createFleetAccount,
    handleOwnError: {
      badrequest: true,
    },
  });

  const editRequest = useApi({
    api: updateFleetAccount,
    handleOwnError: {
      badrequest: true,
    },
    params: {
      fleetId,
    },
  });

  const loading = addRequest.loading || editRequest.loading;

  const submit = (params) => {
    const apiRequest = addMode ? addRequest : editRequest;

    if (addMode) {
      delete params.status;

      if (params.plbType === PLBTypes.PLB_HEDGING) {
        params.baseDieselPrice = Big(params.baseDieselPrice)
          .add(Big(params.accuMovementDiesel || 0))
          .toNumber();
        params.baseGas91Price = Big(params.baseGas91Price)
          .add(Big(params.accuMovementGas91 || 0))
          .toNumber();
        params.baseGas95Price = Big(params.baseGas95Price)
          .add(Big(params.accuMovementGas95 || 0))
          .toNumber();
        params.baseGas97Price = Big(params.baseGas97Price)
          .add(Big(params.accuMovementGas97 || 0))
          .toNumber();
      }
    }

    if (params.representativeEmail) {
      params.representativeEmail = params.representativeEmail.trim();
    }

    if (params.acquirerEmail) {
      params.acquirerEmail = params.acquirerEmail.trim();
    }

    if (params.fleetStations) {
      params.fleetStations = params.fleetStations.map(({ value }) => {
        return value;
      });
    }

    if (params.fleetStationDiscounts) {
      params.fleetStationDiscounts = params.fleetStationDiscounts.map((item) => ({
        stationIds: item.stationIds.value.map((station) => {
          return station.value;
        }),
        discountDiesel: item.discountDiesel.value,
        discountGas91: item.discountGas91.value,
        discountGas95: item.discountGas95.value,
        discountGas97: item.discountGas97.value,
      }));
    }

    if (params.plbType !== PLBTypes.PLB_HEDGING) {
      delete params.accuMovementDiesel;
      delete params.accuMovementGas91;
      delete params.accuMovementGas95;
      delete params.accuMovementGas97;
      delete params.baseDieselPrice;
      delete params.baseGas91Price;
      delete params.baseGas95Price;
      delete params.baseGas97Price;
      delete params.contractPriceDiesel;
      delete params.contractPriceGas91;
      delete params.contractPriceGas95;
      delete params.contractPriceGas97;
      delete params.discountDiesel;
      delete params.discountGas91;
      delete params.discountGas95;
      delete params.discountGas97;
      delete params.freightDiesel;
      delete params.freightGas91;
      delete params.freightGas95;
      delete params.freightGas97;
      delete params.sdumDiesel;
      delete params.sdumGas91;
      delete params.sdumGas95;
      delete params.sdumGas97;
      delete params.fleetStations;
      delete params.depotId;
    }

    handleRequest(
      async () => {
        close();
        await apiRequest.request(
          {
            ...params,
          },
          () => handleSubmit()
        );
        let optionalObject = {};
        if (addMode) {
          optionalObject = {
            secondary: {
              text: locale.goToListOfFleet,
              onClick: () => {
                history.push(Path.FleetAccounts);
              },
            },
          };
        }
        props.show({
          title: locale.exclamatedSuccess,
          image: addMode ? SuccessFormSubmit : SuccessUpdate,
          content: addMode
            ? locale.accountActivationSuccessMessage
            : locale.detailHaveBeenSuccessfullyUpdated,
          primary: {
            text: addMode ? locale.addAnotherAccount : locale.gotIt,
            onClick: () => {
              props.close();
              if (addMode) {
                clearForm();
                history.push(Path.AddFleetAccount);
              } else {
                history.push(Path.ViewFleetAccountById(fleetId));
              }
            },
          },
          ...optionalObject,
        });
      },
      {
        F1006: () => {
          inputRef.current.focus();
          applyFieldErrors({
            shortName: locale.uniqueFuelCodePrefixAlreadyExist,
          });
        },
      }
    );
  };

  const getChangedValues = (compare) => {
    const undefinedToNull = (obj) => {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, value === undefined ? null : value])
      );
    };
    return getObjectDifference(undefinedToNull(previousFormValues), undefinedToNull(compare));
  };

  const handleSubmit = () => {
    const currentFormValues = getFormValues();
    if (currentFormValues.plbType !== PLBTypes.PLB_HEDGING) {
      delete currentFormValues.accuMovementDiesel;
      delete currentFormValues.accuMovementGas91;
      delete currentFormValues.accuMovementGas95;
      delete currentFormValues.accuMovementGas97;
      delete currentFormValues.baseDieselPrice;
      delete currentFormValues.baseGas91Price;
      delete currentFormValues.baseGas95Price;
      delete currentFormValues.baseGas97Price;
      delete currentFormValues.contractPriceDiesel;
      delete currentFormValues.contractPriceGas91;
      delete currentFormValues.contractPriceGas95;
      delete currentFormValues.contractPriceGas97;
      delete currentFormValues.discountDiesel;
      delete currentFormValues.discountGas91;
      delete currentFormValues.discountGas95;
      delete currentFormValues.discountGas97;
      delete currentFormValues.freightDiesel;
      delete currentFormValues.freightGas91;
      delete currentFormValues.freightGas95;
      delete currentFormValues.freightGas97;
      delete currentFormValues.sdumDiesel;
      delete currentFormValues.sdumGas91;
      delete currentFormValues.sdumGas95;
      delete currentFormValues.sdumGas97;
      delete currentFormValues.fleetStations;
      delete currentFormValues.depotId;
      delete currentFormValues.dateTimeOnboarded;
      delete currentFormValues.priceAssessmentDate;
      delete currentFormValues.referenceStationType;
      delete currentFormValues.stationType;
      delete currentFormValues.status;
    }

    const updateParams = getChangedValues(currentFormValues);
    const updatingForm = Object.keys(updateParams).length > 0;
    const validUpdateForm = editMode && updatingForm;
    if (validUpdateForm) {
      setEditConfirmData({ updateParams, stationDepotName });
    }
    if (validUpdateForm || addMode) {
      confirmModal.show({
        title: addMode ? locale.saveDetailsAndSendEmail : locale.areYouSure,
        content: addMode
          ? locale.saveAndSendEmailContentMessage
          : newEmailAdress.flag
          ? locale.accountUpdateSaveWithEmail
          : locale.reviewChangesBelow,
        secondary: {
          text: locale.Cancel,
        },
        primary: {
          text: addMode
            ? locale.saveAndSendEmail
            : newEmailAdress.flag
            ? locale.saveAndSendEmail
            : locale.saveDetails,
          onClick: () => {
            const params = addMode
              ? { ...currentFormValues, fleetStationDiscounts: [...discountCombination] }
              : { ...updateParams };
            submit(params);
          },
        },
      });
    }
  };

  useImperativeHandle(ref, () => ({
    handleUpdate() {
      if (addMode) {
        submitForm(handleSubmit);
      } else {
        handleSubmit();
      }
    },
  }));

  const populateProvince = (name, { value }) => {
    setProvinceKey(value);
    modifyForm({
      [name]: { value },
      [fields.city.name]: {
        value: null,
      },
    });
  };

  const pronvinceData = useMemo(() => {
    if (Provinces) {
      const preparedData = Provinces.map((p) => {
        return {
          label: p.name,
          value: p.name,
        };
      });
      return preparedData;
    }
    return [];
  }, []);

  const setProvinceKey = (value = "") => {
    if (value) {
      const filteredProvince = Provinces.filter((data) => {
        return data.name === value;
      })[0];
      if (filteredProvince) {
        value = filteredProvince.key;
      }
    }
    setProvinceKeyFilter(value);
  };

  const clearProvince = (event, value, reason) => {
    if (reason === "clear") {
      setProvinceKey("");
    } else {
      setProvinceKey(value);
    }
  };

  const cities = useMemo(() => {
    const filteredCities = provinceKeyFilter
      ? Cities.filter((data) => {
          return data.province === provinceKeyFilter;
        })
      : Cities;
    const preparedData = filteredCities.map((p) => {
      return {
        label: p.name,
        value: p.name,
      };
    });
    return preparedData;
  }, [provinceKeyFilter]);

  const onChangeTypeOfBusinessCb = useCallback(
    (type) => {
      modifyField(fields.businessType.name, { value: type });
    },
    [fields.businessType.name, modifyField]
  );

  const onChangeAccountTypeCb = useCallback(
    (name, { value }) => {
      modifyField(name, { value });
      if (value === FleetAccountType.KeyAccount) {
        modifyForm({
          fleetStations: {
            value: [
              {
                value: fields.referenceStationId.value,
                label: fields.referenceStationId.label,
              },
            ],
            isAll: false,
          },
          accountType: {
            value,
          },
        });
      }
    },
    [fields.referenceStationId.label, fields.referenceStationId.value, modifyField, modifyForm]
  );

  const computeAutoPopulatedData = useCallback(
    async (name, { value, label }) => {
      let result;
      if (name === "referenceStationId") {
        modifyField(name, { value, label });

        if (fields.accountType.value === FleetAccountType.KeyAccount) {
          modifyForm({
            fleetStations: {
              value: [
                {
                  value,
                  label,
                },
              ],
              isAll: false,
            },
            [name]: { value, label },
          });
        }

        if (!fields.priceAssessmentDate.value) {
          return;
        }

        result = await getStationPricingRequest({
          stationId: value,
          effectiveDate: formatDate(fields.priceAssessmentDate.value, DateTime.I),
        });
      }

      if (name === "priceAssessmentDate") {
        modifyForm({ [name]: { value } });
        if (!fields.referenceStationId.value) {
          return;
        }
        result = await getStationPricingRequest({
          stationId: fields.referenceStationId.value,
          effectiveDate: value,
        });
      }

      const baseDieselPrice = result[0].pumpPrice;
      const baseGas91Price = result[1].pumpPrice;
      const baseGas95Price = result[2].pumpPrice;
      const baseGas97Price = result[3].pumpPrice;

      const contractPriceDiesel = result[0].latestPumpPrice;
      const contractPriceGas91 = result[1].latestPumpPrice;
      const contractPriceGas95 = result[2].latestPumpPrice;
      const contractPriceGas97 = result[3].latestPumpPrice;

      const accuMovementDiesel = editMode
        ? result[0].accumulatedMovement
        : Big(result[0].latestStationPricingTool?.pumpPrice)
            .minus(result[0].pumpPrice)
            .round(2)
            .toNumber();
      const accuMovementGas91 = editMode
        ? result[1].accumulatedMovement
        : Big(result[1].latestStationPricingTool?.pumpPrice)
            .minus(result[1].pumpPrice)
            .round(2)
            .toNumber();
      const accuMovementGas95 = editMode
        ? result[2].accumulatedMovement
        : Big(result[2].latestStationPricingTool?.pumpPrice)
            .minus(result[2].pumpPrice)
            .round(2)
            .toNumber();
      const accuMovementGas97 = editMode
        ? result[3].accumulatedMovement
        : Big(result[3].latestStationPricingTool?.pumpPrice)
            .minus(result[3].pumpPrice)
            .round(2)
            .toNumber();

      modifyForm({
        [name]: name === "referenceStationId" ? { value, label } : { value },
        depotId: { value: result[0].depot.depotId },
        baseDieselPrice: { value: baseDieselPrice || 0 },
        baseGas91Price: { value: baseGas91Price || 0 },
        baseGas95Price: { value: baseGas95Price || 0 },
        baseGas97Price: { value: baseGas97Price || 0 },
        accuMovementDiesel: { value: accuMovementDiesel || 0 },
        accuMovementGas91: { value: accuMovementGas91 || 0 },
        accuMovementGas95: { value: accuMovementGas95 || 0 },
        accuMovementGas97: { value: accuMovementGas97 || 0 },
        discountDiesel: { value: "0" },
        discountGas91: { value: "0" },
        discountGas95: { value: "0" },
        discountGas97: { value: "0" },
        contractPriceDiesel: { value: contractPriceDiesel || 0 },
        contractPriceGas91: { value: contractPriceGas91 || 0 },
        contractPriceGas95: { value: contractPriceGas95 || 0 },
        contractPriceGas97: { value: contractPriceGas97 || 0 },
        sdumDiesel: { value: result[0].sdum || "0" },
        sdumGas91: { value: result[1].sdum || "0" },
        sdumGas95: { value: result[2].sdum || "0" },
        sdumGas97: { value: result[3].sdum || "0" },
        freightDiesel: { value: result[0].freight || "0" },
        freightGas91: { value: result[1].freight || "0" },
        freightGas95: { value: result[2].freight || "0" },
        freightGas97: { value: result[3].freight || "0" },
      });
    },
    [
      editMode,
      fields.accountType.value,
      fields.priceAssessmentDate.value,
      fields.referenceStationId.value,
      getStationPricingRequest,
      modifyField,
      modifyForm,
    ]
  );

  const selectRedemptionStationCb = useCallback(
    (name, { value, label, isAll }) => {
      // if (!fields.accountType.value) {
      //   applyFieldErrors({
      //     fleetStations: locale.chooseAnAccountCategoryFirst,
      //   });
      //   return;
      // }

      modifyField(name, { value, label, isAll });
    },
    [modifyField]
  );

  const selectDepotCb = useCallback(
    (name, { value, label }) => {
      setStationDepotName({
        ...stationDepotName,
        depotId: label,
      });
      modifyField([name], { value: value, label: label });
    },
    [modifyField, stationDepotName]
  );

  const onChangeShortNameCb = useCallback(
    (name, { value }) => {
      if (maxLength({ value, limit: 5 }) || !value)
        modifyField(name, { value: capitalizeAll(value) });
    },
    [modifyField]
  );

  const onChangeTelephoneCb = useCallback(
    (name, { value }) => {
      if (isTelephoneNumber(value) || !value) modifyField(name, { value });
    },
    [modifyField]
  );

  const onChangePLBTypeCb = useCallback(
    (name, { value }) => {
      const options = [
        {
          label: prettifyIntroductoryPromoOffer(IntroductoryPromoOffer.Basic),
          value: IntroductoryPromoOffer.Basic,
        },
        {
          label: prettifyIntroductoryPromoOffer(IntroductoryPromoOffer.GlobalLinker),
          value: IntroductoryPromoOffer.GlobalLinker,
        },
        {
          label: prettifyIntroductoryPromoOffer(IntroductoryPromoOffer.Mias),
          value: IntroductoryPromoOffer.Mias,
        },
        {
          label: prettifyIntroductoryPromoOffer(IntroductoryPromoOffer.NoIntroductoryPromo),
          value: IntroductoryPromoOffer.NoIntroductoryPromo,
        },
      ];

      if (value === PLBTypes.PLB_HEDGING) {
        options.push({
          label: prettifyIntroductoryPromoOffer(IntroductoryPromoOffer.IntroductoryPlbHedging),
          value: IntroductoryPromoOffer.IntroductoryPlbHedging,
        });
      }

      options.push({
        label: prettifyIntroductoryPromoOffer(IntroductoryPromoOffer.IntroductoryPlbLocqpay),
        value: IntroductoryPromoOffer.IntroductoryPlbLocqpay,
      });

      const isRequired = value === PLBTypes.PLB_HEDGING;
      const isPLBCredit = value === PLBTypes.PLB_LOCQPAY_MANUAL_PO_CREDIT;
      modifyForm({
        [name]: { value },
        [fields.introductoryPromoOffer.name]: { options, value: "" },
        [fields.fleetStations.name]: {
          value: [],
          isAll: false,
        },
        [fields.referenceStationId.name]: {
          value: "",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.accuMovementDiesel.name]: {
          value: "0",
        },
        [fields.accuMovementGas91.name]: {
          value: "0",
        },
        [fields.accuMovementGas95.name]: {
          value: "0",
        },
        [fields.accuMovementGas97.name]: {
          value: "0",
        },
        [fields.baseDieselPrice.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.baseGas91Price.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.baseGas95Price.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.baseGas97Price.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.contractPriceDiesel.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.contractPriceGas91.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.contractPriceGas95.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.contractPriceGas97.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },

        [fields.freightDiesel.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.freightGas91.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.freightGas95.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.freightGas97.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },

        [fields.sdumDiesel.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.sdumGas91.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.sdumGas95.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.sdumGas97.name]: {
          value: "0",
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.estimatedDieselVolume.name]: {
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.estimatedGas91Volume.name]: {
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.estimatedGas95Volume.name]: {
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.estimatedGas97Volume.name]: {
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.existingDieselVolume.name]: {
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.existingGas91Volume.name]: {
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.existingGas95Volume.name]: {
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.existingGas91Volume.name]: {
          required: isRequired,
          validations: {
            isRequired: isRequired,
          },
        },
        [fields.postedBy.name]: {
          required: isPLBCredit,
          validations: {
            isRequired: isPLBCredit,
          },
        },
        [fields.operatorName.name]: {
          required: isPLBCredit,
          validations: [Validation.maxlength(150)],
        },
        [fields.cashBond.name]: {
          required: isPLBCredit,
          validations: [
            Validation.max(99999999.99, {
              customMessage: locale.populate(locale.maximumLimit, ["99,999,999.99"]),
              isFormatted: true,
            }),
          ],
        },
      });
    },
    [modifyForm, fields]
  );

  const handleAddEmail = useCallback(
    (chipValue) => {
      const currentValue = [...fields?.emailAddresses?.value] || [];
      if (!_.find(currentValue, ["emailAddress", chipValue]) && isEmailValid(chipValue)) {
        let emailInput = [];
        if (editMode) {
          const entryType = _.find(previousFormValues.emailAddresses, ["emailAddress", chipValue])
            ? { resend: true }
            : { new: true };

          emailInput = {
            emailAddress: chipValue,
            ...entryType,
          };
        } else {
          emailInput = chipValue;
        }

        currentValue.push(emailInput);
        modifyField(fields.emailAddresses.name, { value: currentValue });
      }
    },
    [
      editMode,
      fields.emailAddresses.name,
      fields.emailAddresses.value,
      modifyField,
      previousFormValues.emailAddresses,
    ]
  );

  const onDeleteEmailAdrresses = (chipValue, index) => {
    const newValue = [...fields?.emailAddresses?.value];
    if (newValue.includes(chipValue)) newValue.splice(index, 1);

    modifyField(fields.emailAddresses.name, { value: newValue });
  };

  const handleStatusChange = (event, deactivate) => {
    const status = deactivate ? AccountStatus.Deactivated : AccountStatus.Active;
    setSwitchStatus(deactivate);
    modifyField(fields.status.name, { value: status });
  };

  useEffect(() => {
    if (editMode) {
      setUpdateLoading(loading);
      const currentFormValues = getFormValues();

      if (currentFormValues.plbType !== PLBTypes.PLB_HEDGING) {
        delete currentFormValues.accuMovementDiesel;
        delete currentFormValues.accuMovementGas91;
        delete currentFormValues.accuMovementGas95;
        delete currentFormValues.accuMovementGas97;
        delete currentFormValues.baseDieselPrice;
        delete currentFormValues.baseGas91Price;
        delete currentFormValues.baseGas95Price;
        delete currentFormValues.baseGas97Price;
        delete currentFormValues.contractPriceDiesel;
        delete currentFormValues.contractPriceGas91;
        delete currentFormValues.contractPriceGas95;
        delete currentFormValues.contractPriceGas97;
        delete currentFormValues.discountDiesel;
        delete currentFormValues.discountGas91;
        delete currentFormValues.discountGas95;
        delete currentFormValues.discountGas97;
        delete currentFormValues.freightDiesel;
        delete currentFormValues.freightGas91;
        delete currentFormValues.freightGas95;
        delete currentFormValues.freightGas97;
        delete currentFormValues.sdumDiesel;
        delete currentFormValues.sdumGas91;
        delete currentFormValues.sdumGas95;
        delete currentFormValues.sdumGas97;
        delete currentFormValues.fleetStations;
        delete currentFormValues.depotId;
      }

      const updateParams = getChangedValues(currentFormValues);
      const fieldsChanged = Object.keys(updateParams).length > 0;
      let newEntries,
        removedEntries = [];
      newEntries =
        currentFormValues.emailAddresses &&
        currentFormValues.emailAddresses.filter((item) => {
          return (
            !_.find(previousFormValues.emailAddresses, ["emailAddress", item.emailAddress]) ||
            item.resend
          );
        });
      removedEntries =
        previousFormValues.emailAddresses &&
        previousFormValues.emailAddresses.filter((item) => {
          return !_.find(currentFormValues.emailAddresses, ["emailAddress", item.emailAddress]);
        });
      setNewEmailAddress({
        ...newEmailAdress,
        flag: newEntries.length > 0,
        emailAddresses: newEntries,
        removedEmailAddresses: removedEntries,
      });

      if (currentFormValues.plbType !== PLBTypes.PLB_HEDGING) {
        setDisableUpdate(!fieldsChanged);
      } else {
        setDisableUpdate(!isFormSubmittable || !fieldsChanged);
      }
    }

    //eslint-disable-next-line
  }, [fields, loading]);

  const addRow = () => {
    setDiscountCombination([
      ...discountCombination,
      {
        stationIds: {
          name: "stationIds",
          visible: false,
          label: locale.eligibleReferenceStation,
          allLabel: locale.referenceStation,
          isAll: true,
          isMultiple: true,
          value: [],
          placeholder: locale.searchOrSelect,
          disableAllOption: true,
          selectAllLabel: locale.all,
          allSelectedText: `All ${locale.referenceStation}`,
        },
        discountDiesel: {
          name: "discountDiesel",
          required: true,
          validations: [Validation.required({ noMessage: true })],
          innerLabel: true,
          parseNumber: true,
          value: "",
          placeholder: "0.00",
        },
        discountGas91: {
          name: "discountGas91",
          required: true,
          validations: [Validation.required({ noMessage: true })],
          innerLabel: true,
          parseNumber: true,
          value: "",
          placeholder: "0.00",
        },
        discountGas95: {
          name: "discountGas95",
          required: true,
          validations: [Validation.required({ noMessage: true })],
          innerLabel: true,
          parseNumber: true,
          value: "",
          placeholder: "0.00",
        },
        discountGas97: {
          name: "discountGas97",
          required: true,
          validations: [Validation.required({ noMessage: true })],
          innerLabel: true,
          parseNumber: true,
          value: "",
          placeholder: "0.00",
        },
      },
    ]);
  };

  const onRemove = (i) => {
    const newForm = [...discountCombination];
    newForm.splice(i, 1);
    setStationsSelected(stationsSelected.filter((station) => station.id !== i));
    setDiscountCombination(newForm);
  };
  const onChangeDieselPrice = (value, i) => {
    const newForm = [...discountCombination];
    newForm[i]["discountDiesel"].value = value;
    setDiscountCombination(newForm);
  };

  const onChangeGas91Price = (value, i) => {
    const newForm = [...discountCombination];
    newForm[i]["discountGas91"].value = value;
    setDiscountCombination(newForm);
  };

  const onChangeGas95Price = (value, i) => {
    const newForm = [...discountCombination];
    newForm[i]["discountGas95"].value = value;
    setDiscountCombination(newForm);
  };

  const onChangeGas97Price = (value, i) => {
    const newForm = [...discountCombination];
    newForm[i]["discountGas97"].value = value;
    setDiscountCombination(newForm);
  };

  const stateForm = {
    switchStatus,
    viewMode,
    addMode,
    editMode,
    otherDetails,
    fields,
    isFormSubmittable,
    modifyField,
    modifyForm,
    loading,
    populateProvince,
    pronvinceData,
    clearProvince,
    cities,
    provinceKeyFilter,
    submitForm,
    handleSubmit,
    onChangeTypeOfBusinessCb,
    onChangeAccountTypeCb,
    handleAddEmail,
    onDeleteEmailAdrresses,
    handleStatusChange,
    selectDepotCb,
    onChangeShortNameCb,
    onChangeTelephoneCb,
    selectRedemptionStationCb,
    inputRef,
    validatedField,
    computeAutoPopulatedData,
    loadingStationPricing,
    computeContractPrice,
    getPriceMovement,
    setDiscountCombination,
    setStationsSelected,
    discountCombination,
    stationsSelected,
    addRow,
    onRemove,
    onChangeDieselPrice,
    onChangeGas91Price,
    onChangeGas95Price,
    onChangeGas97Price,
    onChangePLBTypeCb,
    setFleetCardSelected,
    fleetCardSelected,
  };

  return (
    <div>
      <Loader open={loadingFleet} />
      {!loadingFleet && (
        <div className={styles.container}>
          {!viewMode && (
            <>
              <ConfirmModal
                {...confirmModal}
                className={classNames({
                  [styles.editConfirmModal]: editMode,
                })}
              >
                {editMode && <EditConfirm  
                {...{
                  ...editConfirmData,
                  plbType: fields?.plbType?.value,
                  ...newEmailAdress
                }} />}
              </ConfirmModal>
              <SuccessModal {...props} />
            </>
          )}
          <AccountDetailsFormModule {...stateForm} stationPricings={getStationPricingResult} />
        </div>
      )}
    </div>
  );
});

export default AccountDetailsModule;
